import { cn } from '../lib/utils.js';
import { disabledFade, insetFocusRing } from '../lib/focus.js';

/* ===========================================================================
 * THE SEGMENTED TRACK — one anatomy, shared by the two components that draw it.
 *
 * `Segmented` and `Tabs` are two CONTRACTS over one shape. `Segmented` is a
 * strip with no panel of its own — a scope filter, a range switcher, a view
 * switcher whose region is named by `controls`. `Tabs` is the strip PLUS
 * `Tabs.Content`, for the handful of places where a trigger really does mount
 * and unmount a `tabpanel`. That distinction is worth keeping: a `radiogroup`
 * that filters a table and a `tablist` that swaps a panel announce differently,
 * and only one of them owns panels.
 *
 * What was NOT worth keeping is two drawings of the same object. An audit of
 * every tab-shaped control in the app found the two had drifted on eight
 * separate values while claiming to be the same control:
 *
 *   | | `Tabs` (before) | `Segmented` |
 *   |---|---|---|
 *   | track ground | `bg-muted` | `bg-muted` |
 *   | track inset | `p-1` (4) | `p-[3px]` |
 *   | gap | `gap-1` (4) | `gap-0.5` (2) |
 *   | track floor | none | `min-h-control` |
 *   | overflow | cannot wrap | `max-w-full flex-wrap` |
 *   | segment radius | `rounded-2xs` (2 — the HAIRLINE role) | `rounded-xs` (8) |
 *   | segment type | `text-label` (12.5) | `text-copy font-semibold` (13.5/600) |
 *   | focus ring | outset — drawn over the track's shoulder | `insetFocusRing` |
 *   | disabled | `disabled:opacity-50`, still animating | `disabledFade` + `transition-none` |
 *
 * Three of those are defects rather than differences. The missing wrap is the
 * page-pushes-sideways failure documented on `TRACK_CLASS` below, which `Tabs`
 * carried unfixed because the fix was written in the other file. `rounded-2xs`
 * is the 2px HAIRLINE role (rules §1: radius by role, never by size) spent on a
 * pill inside a 10px track. And a disabled segment that still runs
 * `transition-colors` animates a control that cannot be used, which rules §1
 * forbids outright.
 *
 * So the values live here, once, and both components import them. A change to
 * the shape is now a change to one file rather than a change somebody has to
 * remember to make twice.
 * ======================================================================== */

/**
 * The recessed track. 3px of padding is what makes the pill look inset.
 *
 * ---------------------------------------------------------------------------
 * `max-w-full flex-wrap` — WHY A STRIP IS ALLOWED TO TAKE A SECOND LINE.
 *
 * This was `inline-flex flex-none … ` with `whitespace-nowrap` on every
 * segment, i.e. a box that could neither shrink nor break. That is right while
 * there is room and wrong the moment there is not, and the failure was not
 * subtle: it pushed the page sideways.
 *
 * The chain. `ControlBar` puts each slot in a group that deliberately has NO
 * `min-w-0`, so a cramped bar re-flows onto more lines rather than squashing a
 * field — but a flex item's automatic minimum size is its MIN-CONTENT width,
 * and the min-content width of a track that cannot wrap is the whole strip. So
 * the group could not shrink below it, the bar does not clip, and the strip
 * was painted straight out through the toolbar's rounded border. Because
 * `AppShell`'s content column is `overflow-y-auto` — which computes `overflow-x`
 * to `auto` as well — the whole page then scrolled sideways, taking the sticky
 * toolbar band off the screen with it. Measured in Chrome against these exact
 * classes: the five-segment device-approvals strip is 407px and the
 * five-segment reports strip is 426px, and in the 335px bar of a 375px phone
 * they hung 78px and 98px past the bar's edge.
 *
 * `flex-wrap` drops the track's min-content width to its WIDEST SEGMENT, which
 * is what lets the group shrink; `max-w-full` is what then clamps the track to
 * the space the group was given, so it wraps instead of overflowing. Nothing
 * changes while the strip fits — same 407px, same single 38px row — because
 * `flex-none` still bases it on max-content.
 *
 * WRAPPING RATHER THAN SCROLLING was the choice, and it is now the answer for
 * EVERY strip in the app rather than for the ones that happened to import this
 * file. `overflow-x-auto` on the track would also have released the minimum,
 * and it is the familiar phone pattern, but a segmented strip in this app is a
 * SCOPE FILTER: hiding "Revoked" past a scroll edge means a reader who has
 * never seen it does not know the view exists. It also makes the track a
 * scroll container in BOTH axes, which clips the selected pill's
 * `shadow-resting`. Taking a second line keeps every choice visible and is the
 * same answer `ControlBar` gives one level up. The one screen that wants the
 * scroll instead — Operations' floating panel, whose chrome slot is already an
 * `overflow-x-auto` box — says so at its own call site and gives its reason.
 */
export const SEGMENTED_TRACK_CLASS =
  'inline-flex max-w-full flex-none flex-wrap items-center gap-0.5 rounded-control bg-muted p-[3px] min-h-control';

/**
 * The `pills` row — no track, no inset, and 4px between items rather than 2px
 * (`Hadir Operations.dc.html:219`). It keeps `max-w-full flex-wrap` for exactly
 * the reason the track does: a row that cannot wrap sets its own min-content
 * width as the group's floor and pushes the page sideways on a phone. See the
 * long note above; the failure mode is identical here.
 */
export const SEGMENTED_PILLS_CLASS =
  'inline-flex max-w-full flex-none flex-wrap items-center gap-1';

/**
 * One segment. `insetFocusRing` rather than the outset `focusRing`: the track
 * holds each segment 3px from its own edge, so an outset ring would be drawn
 * over the track's shoulder and — inside a `ControlBar` that clips — partly cut
 * off. Same width and colour, pulled inwards.
 */
export const SEGMENTED_SEGMENT_CLASS = cn(
  'inline-flex items-center justify-center whitespace-nowrap',
  // `text-copy font-semibold` — 13.5px at 600, which is what all four strips in
  // the handoff set (`min-height: 32px; padding: 0 12px; border-radius: 8px;
  // font-size: 13.5px; font-weight: 600`, Hadir Reports.dc.html:361-365). It was
  // `text-label`, whose 600 is right and whose 12.5px is a step small: a segment
  // holds a WORD a reader chooses between, not a field label, so it sits at the
  // body step like the buttons either side of it in the toolbar. The weight has
  // to be said separately because `text-copy` is the 400 body step.
  'text-copy font-semibold',
  '[@media(pointer:coarse)]:min-h-touch',
  'transition-[background-color,box-shadow,color] duration-fast ease-standard',
  // Geometry moved to the per-variant classes below — a tracked segment is the
  // handoff's 32px/12px/8px, a bare pill is its 34px/14px/10px.
  insetFocusRing,
  // A DISABLED CONTROL NEVER ANIMATES (rules §1). `transition-none` is the
  // half of that no token can carry — `disabled:` variants can only turn a
  // transition off, and this is the only place that can be said.
  cn('disabled:pointer-events-none disabled:transition-none', disabledFade)
);

/** Tracked segment geometry — the handoff's 32px / `0 12px` / radius 8. */
export const SEGMENTED_SEGMENT_TRACK_GEOMETRY = 'min-h-8 rounded-xs px-3';

/**
 * Bare-pill geometry — the handoff's 34px / `0 14px` / radius 10
 * (`Hadir Operations.dc.html:221`). `rounded-control` is the 10px role, so no
 * arbitrary value is needed.
 */
export const SEGMENTED_SEGMENT_PILL_GEOMETRY = 'min-h-pill rounded-control px-3.5';

/**
 * ── THE PAGE HEAD'S FLOOR, for a strip that stands in the action cluster ──
 *
 * `size="lg"`. The head's controls stand on one 40px floor — `headerActionHeight`
 * in `apps/web`'s `PageActions`, which exists because that row was measured
 * drawing three heights at once and every component was correct about its own
 * usual home. A `Segmented` is the fourth: 34px as bare pills, 38px as a track,
 * both right in a toolbar and both short beside a 40px button.
 *
 * TWO CONSTANTS BECAUSE THE TWO VARIANTS RAISE DIFFERENT BOXES. A pills row has
 * no track, so its SEGMENTS are the control and they take the floor. A track
 * holds its segments 3px inside itself, so raising the segments would make the
 * control 46px — it is the TRACK that takes the floor, and its segments keep
 * the handoff's 32px pill, centred in it.
 *
 * `min-h-10` and not a named step: 40 is the `Button size="md"` floor
 * (`min-h-10`) that the whole cluster already stands on, and inventing a second
 * name for the same number is how two things that must match come to differ.
 */
export const SEGMENTED_SEGMENT_PILL_LG_GEOMETRY = 'min-h-10 rounded-control px-3.5';
export const SEGMENTED_TRACK_LG_CLASS = 'min-h-10';

/**
 * The raised pale pill. `shadow-resting` is the system's "sitting on a surface"
 * role and is the whole reason the active segment reads as lifted out of the
 * recessed track.
 */
export const SEGMENTED_SEGMENT_SELECTED = 'bg-surface text-foreground shadow-resting';

/** An unchosen segment inside a track — the track is its own hover ground. */
export const SEGMENTED_SEGMENT_IDLE = 'text-foreground-tertiary hover:text-foreground';
